import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MessageSquare, Plus, X, Send, Clock, CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'academic', label: 'Academic' }, { value: 'fees', label: 'Fees' },
  { value: 'transport', label: 'Transport' }, { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'discipline', label: 'Discipline' }, { value: 'teacher_complaint', label: 'Teacher Complaint' },
  { value: 'bullying', label: 'Bullying' }, { value: 'food_quality', label: 'Food Quality' },
  { value: 'safety', label: 'Safety' }, { value: 'exam_related', label: 'Exam Related' },
  { value: 'general', label: 'General' }, { value: 'other', label: 'Other' },
];

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-100 text-slate-500',
  reopened: 'bg-red-100 text-red-700',
};
const PRIORITY_COLORS = { low: 'text-slate-400', medium: 'text-blue-500', high: 'text-amber-500', urgent: 'text-red-500' };

export default function TicketManagement() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [form, setForm] = useState({ category: 'general', subject: '', description: '', priority: 'medium' });

  const isAdmin = user?.role === 'school_admin' || user?.role === 'hr';

  const fetchTickets = async () => {
    const params = new URLSearchParams({ page: 1, limit: 50 });
    if (filterStatus) params.set('status', filterStatus);
    if (filterCategory) params.set('category', filterCategory);
    try {
      const res = await axios.get(`${API}/tickets?${params}`, { headers });
      setTickets(res.data.data.tickets || []);
      setTotal(res.data.data.total || 0);
    } catch {}
    setLoading(false);
  };

  const fetchStats = async () => {
    try { const res = await axios.get(`${API}/tickets/stats/overview`, { headers }); setStats(res.data.data); } catch {}
  };

  useEffect(() => { fetchTickets(); fetchStats(); }, [filterStatus, filterCategory]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tickets`, form, { headers });
      toast.success('Ticket created');
      setShowForm(false);
      setForm({ category: 'general', subject: '', description: '', priority: 'medium' });
      fetchTickets(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/tickets/${selectedTicket.ticket_id}/reply`, { message: replyText }, { headers });
      toast.success('Reply sent');
      setReplyText('');
      const res = await axios.get(`${API}/tickets/${selectedTicket.ticket_id}`, { headers });
      setSelectedTicket(res.data.data);
      fetchTickets();
    } catch (err) { toast.error('Failed to send reply'); }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}/status`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchTickets(); fetchStats();
      if (selectedTicket?.ticket_id === ticketId) {
        const res = await axios.get(`${API}/tickets/${ticketId}`, { headers });
        setSelectedTicket(res.data.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="ticket-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="ticket-management-title">Grievance / Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">{total} total tickets</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="create-ticket-btn"><Plus className="h-4 w-4 mr-2" /> Raise Ticket</Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3 text-center"><p className="text-xs text-slate-400">Total</p><p className="text-xl font-bold text-slate-900">{stats.total}</p></div>
          <div className="bg-white rounded-xl border border-blue-100 p-3 text-center"><p className="text-xs text-blue-400">Open</p><p className="text-xl font-bold text-blue-600">{stats.by_status?.open || 0}</p></div>
          <div className="bg-white rounded-xl border border-amber-100 p-3 text-center"><p className="text-xs text-amber-400">In Progress</p><p className="text-xl font-bold text-amber-600">{stats.by_status?.in_progress || 0}</p></div>
          <div className="bg-white rounded-xl border border-emerald-100 p-3 text-center"><p className="text-xs text-emerald-400">Resolved</p><p className="text-xl font-bold text-emerald-600">{stats.by_status?.resolved || 0}</p></div>
          <div className="bg-white rounded-xl border border-slate-100 p-3 text-center"><p className="text-xs text-slate-400">Closed</p><p className="text-xl font-bold text-slate-500">{stats.by_status?.closed || 0}</p></div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
        </Select>
        <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-9"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Ticket List */}
      {loading ? <div className="text-center py-12 mt-6 text-slate-400">Loading...</div> : tickets.length === 0 ? (
        <div className="text-center py-16 mt-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-testid="no-tickets"><MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">No tickets found</p></div>
      ) : (
        <div className="space-y-2 mt-6">
          {tickets.map(t => (
            <div key={t.ticket_id} onClick={() => setSelectedTicket(t)} className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:shadow-md transition-shadow" data-testid={`ticket-${t.ticket_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono ${PRIORITY_COLORS[t.priority]}`}>{t.ticket_number}</span>
                  <h3 className="font-medium text-sm text-slate-900">{t.subject}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{t.category?.replace('_', ' ')}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status]}`}>{t.status?.replace('_', ' ')}</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1.5">by {t.created_by_name} ({t.created_by_role}) &middot; {new Date(t.created_at).toLocaleDateString()} &middot; {(t.replies || []).length} replies</p>
            </div>
          ))}
        </div>
      )}

      {/* Ticket Detail Drawer */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelectedTicket(null)}>
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail-drawer">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
              <div>
                <p className="text-xs font-mono text-slate-400">{selectedTicket.ticket_number}</p>
                <h2 className="text-lg font-bold text-slate-900">{selectedTicket.subject}</h2>
              </div>
              <button onClick={() => setSelectedTicket(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[selectedTicket.status]}`}>{selectedTicket.status?.replace('_', ' ')}</span>
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full capitalize">{selectedTicket.category?.replace('_', ' ')}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_COLORS[selectedTicket.priority]} bg-slate-50 capitalize`}>{selectedTicket.priority}</span>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{selectedTicket.description}</p>
              <p className="text-xs text-slate-400">Raised by {selectedTicket.created_by_name} on {new Date(selectedTicket.created_at).toLocaleDateString()}</p>

              {isAdmin && (
                <div className="flex gap-2">
                  {['in_progress', 'resolved', 'closed'].map(s => (
                    <button key={s} onClick={() => handleStatusChange(selectedTicket.ticket_id, s)} className={`text-xs px-3 py-1.5 rounded-lg border ${selectedTicket.status === s ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              )}

              {/* Replies */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Conversation</h4>
                {(selectedTicket.replies || []).map(r => (
                  <div key={r.reply_id} className={`p-3 rounded-xl text-sm ${r.user_role === 'parent' ? 'bg-violet-50 ml-4' : 'bg-teal-50 mr-4'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-semibold text-slate-600">{r.user_name} <span className="text-slate-400 font-normal capitalize">({r.user_role})</span></span>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-700">{r.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="flex gap-2">
                <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type a reply..." className="flex-1" onKeyDown={(e) => e.key === 'Enter' && handleReply()} data-testid="ticket-reply-input" />
                <Button onClick={handleReply} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="ticket-send-reply"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-ticket-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Raise a Ticket</h2><button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs text-slate-500">Subject *</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder="Brief summary of the issue" data-testid="ticket-subject-input" /></div>
              <div><Label className="text-xs text-slate-500">Description *</Label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Describe the issue in detail..." rows={4} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500" data-testid="ticket-description-input" /></div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="submit-ticket-btn">Submit Ticket</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
